import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../api/client';

// One editable email template. `subjectKey`/`bodyKey` are the settings payload
// fields the backend reads (see routers/admin.py::_settings_payload) and the
// tokens the backend substitutes per-FI (routers/emails.py).
type Template = {
  id: string;
  title: string;
  blurb: string;
  subjectKey: string;
  bodyKey: string;
  tokens: { token: string; desc: string }[];
};

const TEMPLATES: Template[] = [
  {
    id: 'invite',
    title: 'Schedule Priority Call',
    blurb: 'Drafted when scheduling an FI’s Priority Call (shown on the FI Action Items page).',
    subjectKey: 'inviteEmailSubject',
    bodyKey: 'inviteEmailBody',
    tokens: [
      { token: '{{greeting}}', desc: 'Recipients’ first names, e.g. "Sean and Maria"' },
      { token: '{{fi_name}}', desc: 'The FI’s name' },
      { token: '{{year}}', desc: 'Current program year label, e.g. "FY26"' },
      { token: '{{proposed_times}}', desc: 'Proposed call times (from Outlook availability)' },
    ],
  },
  {
    id: 'cal',
    title: 'CAL Pre-Call Briefing',
    blurb: 'Reminder sent to the FI’s CAL ahead of the Priority Call (shown on the CAL Notification page).',
    subjectKey: 'calEmailSubject',
    bodyKey: 'calEmailBody',
    tokens: [
      { token: '{{cal_first_name}}', desc: 'The CAL’s first name' },
      { token: '{{fi_name}}', desc: 'The FI’s name' },
      { token: '{{year}}', desc: 'Current program year label, e.g. "FY26"' },
      { token: '{{call_date}}', desc: 'The scheduled call date (or "a date to be confirmed")' },
    ],
  },
  {
    id: 'thankyou',
    title: 'Post-Call Thank-You + Referral',
    blurb: 'Sent after the call with the one-pager and the FI’s referral link.',
    subjectKey: 'thankyouEmailSubject',
    bodyKey: 'thankyouEmailBody',
    tokens: [
      { token: '{{greeting}}', desc: 'Recipients’ first names, e.g. "Sean and Maria"' },
      { token: '{{fi_name}}', desc: 'The FI’s name' },
      { token: '{{year}}', desc: 'Current program year label, e.g. "FY26"' },
      { token: '{{referral_link}}', desc: 'The FI’s unique referral URL' },
    ],
  },
];

function TemplateCard({ tpl, settings, onSaved }: { tpl: Template; settings: any; onSaved: (data: any) => void }) {
  const [subject, setSubject] = useState<string>(settings[tpl.subjectKey] ?? '');
  const [body, setBody] = useState<string>(settings[tpl.bodyKey] ?? '');
  const [origSubject, setOrigSubject] = useState<string>(settings[tpl.subjectKey] ?? '');
  const [origBody, setOrigBody] = useState<string>(settings[tpl.bodyKey] ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const dirty = subject !== origSubject || body !== origBody;

  const apply = (data: any) => {
    setSubject(data?.[tpl.subjectKey] ?? '');
    setBody(data?.[tpl.bodyKey] ?? '');
    setOrigSubject(data?.[tpl.subjectKey] ?? '');
    setOrigBody(data?.[tpl.bodyKey] ?? '');
    onSaved(data);
  };

  const save = async (subj: string, bod: string, resetMsg: boolean) => {
    setSaving(true);
    setError('');
    try {
      const data = await API.updateSettings({ [tpl.subjectKey]: subj, [tpl.bodyKey]: bod });
      apply(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError(resetMsg ? 'Failed to reset. Please try again.' : 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!window.confirm(`Reset "${tpl.title}" to the built-in default template? Any customisations will be lost.`)) return;
    // Empty strings tell the server to fall back to the code default.
    save('', '', true);
  };

  return (
    <div className="phil-card" style={{ marginBottom: 20 }}>
      <div className="phil-card-head">
        <h2>{tpl.title}</h2>
        <span style={{ fontSize: 12, color: '#888' }}>Sender, recipients &amp; attachment are added automatically</span>
      </div>
      <div style={{ padding: '18px 24px' }}>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>{tpl.blurb}</div>

        <div style={{ background: '#f5f7fa', border: '1px solid #e2e2e2', borderRadius: 6, padding: '12px 16px', marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 }}>
            Available Tokens — substituted per-FI
          </div>
          {tpl.tokens.map(t => (
            <div key={t.token} style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 6 }}>
              <code style={{ background: '#fff', border: '1px solid #e2e2e2', borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 700, color: '#0369a1', whiteSpace: 'nowrap' }}>
                {t.token}
              </code>
              <span style={{ fontSize: 12, color: '#777' }}>{t.desc}</span>
            </div>
          ))}
        </div>

        <div className="phil-form-field">
          <div className="phil-q-label">Subject</div>
          <input
            className="phil-input"
            style={{ width: '100%' }}
            value={subject}
            onChange={e => { setSubject(e.target.value); setSaved(false); }}
          />
        </div>

        <div className="phil-form-field" style={{ marginTop: 16 }}>
          <div className="phil-q-label">Body</div>
          <textarea
            className="phil-textarea"
            style={{ height: 280, fontFamily: 'inherit', lineHeight: 1.6 }}
            value={body}
            onChange={e => { setBody(e.target.value); setSaved(false); }}
          />
          <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>{body.length} characters</div>
        </div>

        {error && <div className="phil-error" style={{ marginBottom: 12 }}>{error}</div>}
        {saved && <div className="phil-saved-banner" style={{ marginBottom: 12 }}>Saved — future drafts will use the updated template.</div>}

        <button className="phil-btn phil-btn-primary" onClick={() => save(subject, body, false)} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        {dirty && !saving && (
          <button
            className="phil-btn phil-btn-secondary"
            style={{ marginLeft: 10 }}
            onClick={() => { setSubject(origSubject); setBody(origBody); setSaved(false); }}
          >
            Discard
          </button>
        )}
        <button className="phil-btn phil-btn-secondary" style={{ marginLeft: 10 }} onClick={handleReset} disabled={saving}>
          Reset to Default
        </button>
      </div>
    </div>
  );
}

export default function AdminEmailTemplates() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.getSettings().then(data => {
      setSettings(data ?? {});
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="phil-loading">Loading…</div>;

  return (
    <>
      <div className="breadcrumb">
        <a onClick={() => navigate('/admin/settings')}>Admin Settings</a>
        <span className="bc-sep">›</span>
        <span>Email Templates</span>
      </div>

      <div className="phil-page-heading">Email Templates</div>
      <div className="phil-page-sub">
        The base emails the platform drafts for FI outreach. Edit the subject and body; tokens are
        filled in per-FI when each draft is generated.
      </div>

      <div style={{ marginTop: 20 }}>
        {TEMPLATES.map(tpl => (
          <TemplateCard
            key={tpl.id}
            tpl={tpl}
            settings={settings}
            onSaved={data => setSettings((prev: any) => ({ ...prev, ...data }))}
          />
        ))}
      </div>
    </>
  );
}
